/**
 * Aba "Conectar ao Claude" do Perfil — gera o token pessoal do conector MCP
 * (consulta somente leitura dos dados da clínica) e mostra como ligar no Claude.
 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Button, TextField, Alert, Chip, IconButton, Tooltip,
  CircularProgress, Divider,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddLinkIcon from '@mui/icons-material/AddLink';
import CheckIcon from '@mui/icons-material/Check';
import api from '../services/api';

const ORANGE = '#F97316';

const fmtData = (iso) => (iso ? new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : null);

function CampoCopiavel({ label, valor, mono = true }) {
  const [copiado, setCopiado] = useState(false);
  const copiar = async () => {
    try { await navigator.clipboard.writeText(valor); setCopiado(true); setTimeout(() => setCopiado(false), 2000); } catch { /* sem permissão de clipboard */ }
  };
  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
        <Box sx={{ flex: 1, minWidth: 0, p: '8px 12px', borderRadius: '8px', border: '1px solid rgba(0,0,0,0.12)', bgcolor: 'rgba(0,0,0,0.02)', fontFamily: mono ? 'monospace' : undefined, fontSize: '0.8rem', wordBreak: 'break-all' }}>
          {valor}
        </Box>
        <Tooltip title={copiado ? 'Copiado!' : 'Copiar'}>
          <IconButton onClick={copiar} size="small" sx={{ color: copiado ? '#16a34a' : ORANGE }}>
            {copiado ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}

export default function ConexaoClaude() {
  const [conexoes, setConexoes] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [nome, setNome] = useState('Claude');
  const [criando, setCriando] = useState(false);
  const [erro, setErro] = useState('');
  const [novoToken, setNovoToken] = useState(null);

  const carregar = useCallback(async () => {
    try {
      const r = await api.get('/api-tokens');
      setConexoes(r.tokens || []);
    } catch (e) {
      setErro(e.response?.data?.message || 'Não foi possível carregar as conexões.');
    } finally { setCarregando(false); }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const gerar = async () => {
    setCriando(true); setErro('');
    try {
      const r = await api.post('/api-tokens', { nome });
      setNovoToken(r.token);
      setNome('Claude');
      carregar();
    } catch (e) {
      setErro(e.response?.data?.message || 'Não foi possível gerar a conexão.');
    } finally { setCriando(false); }
  };

  const revogar = async (c) => {
    if (!window.confirm(`Revogar a conexão "${c.nome}"?\nQuem estiver usando ela no Claude perde o acesso na hora.`)) return;
    try {
      await api.delete(`/api-tokens/${c.id}`);
      setConexoes((l) => l.filter((x) => x.id !== c.id));
      if (novoToken && novoToken.startsWith(c.prefixo)) setNovoToken(null);
    } catch (e) { setErro(e.response?.data?.message || 'Não foi possível revogar.'); }
  };

  const urlConector = novoToken ? `${api.defaults.baseURL}/mcp/${novoToken}` : '';

  return (
    <Box sx={{ maxWidth: 760 }}>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>Conectar ao Claude</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Deixe o Claude consultar os dados da sua clínica (somente leitura) para montar relatórios, atualizar planilhas do Notion e responder perguntas como
        {' '}<i>"quanto faturei em setembro?"</i>.
      </Typography>

      <Box sx={{ mb: 2.5, p: 2, borderRadius: '10px', bgcolor: 'rgba(249,115,22,0.06)', border: '1px solid rgba(249,115,22,0.15)' }}>
        <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>O que o Claude consegue ver</Typography>
        <Typography variant="body2" color="text.secondary">
          Resumo do mês, faturamentos, despesas, pacientes (nome, telefone, e-mail e nascimento, sem CPF), agenda e orçamentos da <b>sua clínica</b>.
          Ele <b>não altera nada</b> no Gerencie.
        </Typography>
      </Box>

      {erro && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErro('')}>{erro}</Alert>}

      {novoToken && (
        <Alert severity="success" icon={false} sx={{ mb: 2.5, alignItems: 'flex-start' }}>
          <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>Conexão criada! Copie agora — por segurança ela só aparece desta vez.</Typography>
          <CampoCopiavel label="Endereço do conector (cole no Claude)" valor={urlConector} />
          <Typography variant="body2" sx={{ mt: 1.5, fontWeight: 700 }}>Como ligar no Claude</Typography>
          <Box component="ol" sx={{ pl: 2.5, my: 0.5, fontSize: '0.85rem' }}>
            <li>No Claude, abra <b>Configurações → Conectores</b> e clique em <b>Adicionar conector personalizado</b>.</li>
            <li>Nome: <b>Gerencie Odonto</b>. Em endereço (URL), cole o endereço acima e confirme.</li>
            <li>Em uma conversa, ative o conector e peça, por exemplo: <i>"Resuma meu faturamento e despesas de setembro"</i>.</li>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Trate esse endereço como uma senha: quem tiver ele consulta os dados da sua clínica. Se vazar, é só revogar abaixo.
          </Typography>
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', mb: 3 }}>
        <TextField size="small" label="Nome da conexão" value={nome} onChange={(e) => setNome(e.target.value)} inputProps={{ maxLength: 80 }} sx={{ minWidth: 220 }} />
        <Button
          variant="contained" disableElevation startIcon={criando ? <CircularProgress size={16} color="inherit" /> : <AddLinkIcon />}
          onClick={gerar} disabled={criando || !nome.trim()}
          sx={{ bgcolor: ORANGE, '&:hover': { bgcolor: '#EA580C' }, borderRadius: '8px', fontWeight: 700, textTransform: 'none' }}
        >
          Gerar nova conexão
        </Button>
      </Box>

      <Divider sx={{ mb: 2 }} />
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Conexões ativas</Typography>
      {carregando ? (
        <CircularProgress size={22} sx={{ color: ORANGE }} />
      ) : conexoes.length === 0 ? (
        <Typography variant="body2" color="text.disabled">Nenhuma conexão ainda.</Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {conexoes.map((c) => (
            <Box key={c.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, p: '10px 14px', borderRadius: '10px', border: '1px solid rgba(0,0,0,0.1)' }}>
              <Box sx={{ minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.9rem' }} noWrap>{c.nome}</Typography>
                  <Chip label={`${c.prefixo}…`} size="small" sx={{ height: 20, fontFamily: 'monospace', fontSize: '0.7rem' }} />
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Criada em {fmtData(c.createdAt)} · {c.ultimoUsoEm ? `último uso em ${fmtData(c.ultimoUsoEm)}` : 'ainda não usada'}
                </Typography>
              </Box>
              <Tooltip title="Revogar conexão">
                <IconButton size="small" onClick={() => revogar(c)} sx={{ color: 'text.disabled', '&:hover': { color: '#ef4444' } }}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}
